import { Box, Chip, Stack, Tab, TabList } from "@compass/core";
import {
  LkStudentTabId,
  Permission,
  PermissionAction,
  Student,
  StudentViewResource,
  TenantInfo,
  TextingOptIn,
} from "generated/openApi";
import "moment-timezone";
import ErrorIcon from "@mui/icons-material/Error";
import { isAuthorized } from "services";
import { StudentTab } from "./StudentPage";

const OPTED_OUT_OPT_IN_METHOD_ID = 4;

export const StudentTabs = (props: {
  setCurrentTab: (value: StudentTab) => void;
  student: Student;
  textingOptIn: TextingOptIn | undefined;
  viewResource: StudentViewResource;
  tenantInfo: TenantInfo | undefined;
  hasMissingAprData: boolean;
}) => {
  const tabs: StudentTab[] = initTabs();

  function normalizePhoneNumber(phoneNumber?: string): string {
    const digits = phoneNumber?.replace(/\D/g, "") ?? "";

    if (digits.length === 11 && digits.startsWith("1")) return digits.substring(1);

    return digits;
  }

  function studentCanText(): boolean {
    const studentCellPhone = normalizePhoneNumber(props.student.cellPhone);
    const optInPhoneNumber = normalizePhoneNumber(props.textingOptIn?.phoneNumber);

    return (
      studentCellPhone.length > 0 &&
      props.textingOptIn?.optedIn === true &&
      props.textingOptIn?.optInMethodId !== OPTED_OUT_OPT_IN_METHOD_ID &&
      !props.textingOptIn?.optOutDate &&
      (!optInPhoneNumber || optInPhoneNumber === studentCellPhone)
    );
  }

  function initTabs(): StudentTab[] {
    let tabs: StudentTab[] = [];
    const studentTabIds = props.viewResource.studentTabs?.map((x) => x.id);

    tabs.push("Profile");

    if (
      isAuthorized(Permission.StudentService, PermissionAction.View) &&
      studentTabIds?.includes(LkStudentTabId.Service)
    )
      tabs.push("Services");

    if (
      (isAuthorized(Permission.StudentGrade, PermissionAction.View) && studentTabIds?.includes(LkStudentTabId.Grade)) ||
      (isAuthorized(Permission.StudentGpa, PermissionAction.View) && studentTabIds?.includes(LkStudentTabId.Gpa)) ||
      (isAuthorized(Permission.StudentTest, PermissionAction.View) && studentTabIds?.includes(LkStudentTabId.Test)) ||
      (isAuthorized(Permission.StudentAbsence, PermissionAction.View) &&
        studentTabIds?.includes(LkStudentTabId.Absence))
    )
      tabs.push("Transcript");

    if (isAuthorized(Permission.StudentFile, PermissionAction.View)) tabs.push("Forms");

    if (
      isAuthorized(Permission.StudentApplication, PermissionAction.View) &&
      studentTabIds?.includes(LkStudentTabId.ApplicationResponse)
    )
      tabs.push("Applications");

    if (
      isAuthorized(Permission.StudentNscCollege, PermissionAction.View) &&
      studentTabIds?.includes(LkStudentTabId.Nsc)
    )
      tabs.push("Post-Secondary");

    if (studentTabIds?.includes(LkStudentTabId.AprUb)) 
      tabs.push("APR-UB");

    if (
      isAuthorized(Permission.StudentFile, PermissionAction.View) &&
      studentTabIds?.includes(LkStudentTabId.File)
    )
      tabs.push("Files");

    if (
      props.tenantInfo?.canInfobip &&
      studentCanText() &&
      isAuthorized(Permission.Texting, PermissionAction.View) &&
      studentTabIds?.includes(LkStudentTabId.Texting)
    )
      tabs.push("Texting");

    return tabs;
  }

  const tabTotals: [StudentTab, number | undefined][] = [
    ["Services", props.viewResource.servicesTotal],
    ["Transcript", props.viewResource.transcriptYearsTotal],
    ["Forms", props.viewResource.formsTotal],
    ["Applications", props.viewResource.applicationsTotal],
    ["Post-Secondary", props.viewResource.nscCollegesTotal],
    ["Files", props.viewResource.filesTotal],
    ["Texting", props.viewResource.textingTotal],
  ];

  return (
    <TabList
      onChange={(e, value) => props.setCurrentTab(value)}
      variant="scrollable"
      scrollButtons
      allowScrollButtonsMobile
    >
      {tabs.map((tab) => {
        const total: number | undefined = tabTotals.find((x) => x[0] === tab)?.[1];
        let displayName: string = tab;
        if (tab === "APR-UB") displayName = "APR";

        return (
          <Tab
            key={tab}
            label={
              <Stack direction="row" spacing="sm" alignItems="center">
                <Box>{displayName}</Box>
                {tab === "APR-UB" && props.hasMissingAprData && (
                  <ErrorIcon color="error" fontSize="small" aria-label="APR data is missing" />
                )}
                {total !== undefined && total !== null && (
                  <Chip label={total} size="small" color={tab === "Texting" && total > 0 ? "error" : undefined} />
                )}
              </Stack>
            }
            value={tab}
          />
        );
      })}
    </TabList>
  );
};
